import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  IconDownload,
  IconFile,
  IconLoader2,
  IconMicrophone2,
  IconPackageExport,
  IconPaperclip,
  IconPhoto,
  IconTrash,
  IconUpload,
  IconCalendar,
} from '@tabler/icons-react';
import { useDropzone } from 'react-dropzone';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { ImageLightbox } from '../ImageLightbox';
import { PdfLightbox } from '../PdfLightbox';
import { downloadFileByUrl } from '../../lib/utils';

interface AttachmentsUploaderProps {
  attachments: string[];
  isLoading: boolean;
  isUploading: boolean;
  deletingFileName: string | null;
  getFileUrl: (filename: string, download?: boolean) => string;
  getFileUrlFresh: (filename: string, download?: boolean) => Promise<string>;
  onUpload: (files: File[]) => void | Promise<void>;
  onDelete: (filename: string) => void | Promise<void>;
  filesDate?: string;
}

const formatDate = (isoDate: string): string => {
  const d = new Date(isoDate);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const isImageFile = (filename: string): boolean => /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(filename);
const isAudioFile = (filename: string): boolean => /\.(mp3|ogg|wav|m4a|aac|flac)$/i.test(filename);
const isPdfFile = (filename: string): boolean => /\.pdf$/i.test(filename);

const SPEEDS = [1, 1.5, 2] as const;
type Speed = typeof SPEEDS[number];

interface AudioAttachmentProps {
  filename: string;
  fileUrl: string;
  filesDate?: string;
  onDownload: () => void;
  getFileUrlFresh: (filename: string, download?: boolean) => Promise<string>;
}

const AudioAttachment: React.FC<AudioAttachmentProps> = ({ filename, fileUrl, filesDate, onDownload, getFileUrlFresh }) => {
  const [speed, setSpeed] = useState<Speed>(1);
  const playerRef = useRef<AudioPlayer>(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    const audio = playerRef.current?.audio?.current;
    if (audio) audio.playbackRate = speed;
  }, [speed]);

  const cycleSpeed = useCallback(() => {
    setSpeed(prev => {
      const idx = SPEEDS.indexOf(prev);
      return SPEEDS[(idx + 1) % SPEEDS.length];
    });
  }, []);

  const handlePlayerClickCapture = useCallback(async (e: React.MouseEvent) => {
    if (isRefreshingRef.current) return;
    const audio = playerRef.current?.audio?.current;
    if (!audio?.paused) return;

    e.stopPropagation();
    e.preventDefault();
    isRefreshingRef.current = true;
    try {
      const freshUrl = await getFileUrlFresh(filename);
      audio.src = freshUrl;
      audio.load();
      void audio.play();
    } catch {
      void audio.play();
    } finally {
      isRefreshingRef.current = false;
    }
  }, [filename, getFileUrlFresh]);

  return (
    <div className="attachment-audio">
      <div className="attachment-audio-header">
        <div className="attachment-audio-info">
          <IconMicrophone2 size={14} stroke={2} />
          {filesDate && (
            <span className="attachment-audio-date">
              <IconCalendar size={12} stroke={2} />
              {formatDate(filesDate)}
            </span>
          )}
        </div>
        <div className="attachment-audio-controls">
          <button
            className="attachment-audio-speed"
            type="button"
            onClick={cycleSpeed}
            title="Скорость воспроизведения"
          >
            {speed}x
          </button>
          <button
            className="attachment-action"
            type="button"
            onClick={onDownload}
            aria-label={`Скачать ${filename}`}
          >
            <IconDownload size={15} stroke={2} />
          </button>
        </div>
      </div>
      <div onClickCapture={handlePlayerClickCapture}>
        <AudioPlayer
          ref={playerRef}
          src={fileUrl}
          showJumpControls={false}
          showSkipControls={false}
          customAdditionalControls={[]}
          customVolumeControls={[]}
          layout="horizontal"
        />
      </div>
    </div>
  );
};

const AttachmentsUploader: React.FC<AttachmentsUploaderProps> = ({
  attachments,
  isLoading,
  isUploading,
  deletingFileName,
  getFileUrl,
  getFileUrlFresh,
  onUpload,
  onDelete,
  filesDate,
}) => {
  const [preview, setPreview] = useState<{ src: string; filename: string } | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{ src: string; filename: string } | null>(null);
  const [loadingFile, setLoadingFile] = useState<string | null>(null);

  const handleDrop = useCallback((files: File[]) => {
    if (!files.length || isUploading) {
      return;
    }
    void onUpload(files);
  }, [isUploading, onUpload]);

  const handleDownloadAll = useCallback(() => {
    attachments.forEach((filename, index) => {
      window.setTimeout(() => {
        void getFileUrlFresh(filename, true).then(url => downloadFileByUrl(url, filename));
      }, index * 300);
    });
  }, [attachments, getFileUrlFresh]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop: handleDrop,
    multiple: true,
    disabled: isUploading,
  });

  return (
    <>
    <div className="attachments-box">
      <div className="attachments-toolbar">
        <div
          {...getRootProps({
            className: `attachments-upload${isUploading ? ' is-disabled' : ''}${isDragActive ? ' is-drag-active' : ''}${isDragReject ? ' is-drag-reject' : ''}`,
          })}
        >
          <input {...getInputProps({ name: 'attachments' })} />
          {isUploading ? (
            <IconLoader2 size={16} stroke={2} className="spin" />
          ) : (
            <IconUpload size={16} stroke={2} />
          )}
          <span>
            {isUploading
              ? 'Загрузка...'
              : isDragActive
                ? 'Отпустите файлы'
                : 'Загрузить файлы'}
          </span>
        </div>
        {attachments.length > 1 && (
          <button
            className="attachments-download-all"
            type="button"
            onClick={handleDownloadAll}
            title="Скачать все файлы"
          >
            <IconPackageExport size={16} stroke={2} />
            <span>Скачать все</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="attachments-empty">Загружаем вложения...</div>
      ) : (
        <div className="attachments-list">
          {attachments.map(filename => {
            const image = isImageFile(filename);
            const audio = isAudioFile(filename);
            const fileUrl = getFileUrl(filename);
            const isDeleting = deletingFileName === filename;

            const pdf = isPdfFile(filename);

            return (
              <div className={`attachment-item${audio ? ' is-audio' : ''}`} key={filename}>
                {audio ? (
                  <AudioAttachment
                    filename={filename}
                    fileUrl={fileUrl}
                    filesDate={filesDate}
                    onDownload={() => { void getFileUrlFresh(filename, true).then(url => downloadFileByUrl(url, filename)); }}
                    getFileUrlFresh={getFileUrlFresh}
                  />
                ) : (
                  <>
                    {image ? (
                      <button
                        className="attachment-preview is-image"
                        type="button"
                        disabled={loadingFile === filename}
                        onClick={() => {
                          if (loadingFile) return;
                          setLoadingFile(filename);
                          void getFileUrlFresh(filename)
                            .then(src => setPreview({ src, filename }))
                            .finally(() => setLoadingFile(null));
                        }}
                        aria-label={`Открыть ${filename}`}
                      >
                        {loadingFile === filename
                          ? <IconLoader2 size={20} stroke={2} className="spin" />
                          : <img src={fileUrl} alt={filename} />}
                      </button>
                    ) : pdf ? (
                      <button
                        className="attachment-preview is-pdf"
                        type="button"
                        disabled={loadingFile === filename}
                        onClick={() => {
                          if (loadingFile) return;
                          setLoadingFile(filename);
                          void getFileUrlFresh(filename)
                            .then(src => setPdfPreview({ src, filename }))
                            .finally(() => setLoadingFile(null));
                        }}
                        aria-label={`Открыть ${filename}`}
                      >
                        {loadingFile === filename
                          ? <IconLoader2 size={18} stroke={2} className="spin" />
                          : <><IconFile size={18} stroke={1.8} /><IconPaperclip size={14} stroke={2} className="attachment-corner-icon" /></>}
                      </button>
                    ) : (
                      <button
                        className="attachment-preview"
                        type="button"
                        disabled={loadingFile === filename}
                        onClick={() => {
                          if (loadingFile) return;
                          setLoadingFile(filename);
                          void getFileUrlFresh(filename)
                            .then(url => window.open(url, '_blank', 'noreferrer'))
                            .finally(() => setLoadingFile(null));
                        }}
                        aria-label={`Открыть ${filename}`}
                      >
                        {loadingFile === filename
                          ? <IconLoader2 size={18} stroke={2} className="spin" />
                          : <><IconFile size={18} stroke={1.8} /><IconPaperclip size={14} stroke={2} className="attachment-corner-icon" /></>}
                      </button>
                    )}
                    <div className="attachment-meta">
                      <div className="attachment-kind">
                        {image ? (
                          <>
                            <IconPhoto size={13} stroke={2} />
                            Изображение
                          </>
                        ) : pdf ? (
                          <>
                            <IconFile size={13} stroke={2} />
                            PDF
                          </>
                        ) : (
                          <>
                            <IconFile size={13} stroke={2} />
                            Файл
                          </>
                        )}
                      </div>
                    </div>
                    <div className="attachment-actions">
                      <button
                        className="attachment-action"
                        type="button"
                        onClick={() => { void getFileUrlFresh(filename, true).then(url => downloadFileByUrl(url, filename)); }}
                        aria-label={`Скачать ${filename}`}
                      >
                        <IconDownload size={15} stroke={2} />
                      </button>
                      <button
                        className="attachment-action danger"
                        type="button"
                        onClick={() => { void onDelete(filename); }}
                        disabled={isDeleting}
                        aria-label={`Удалить ${filename}`}
                      >
                        {isDeleting ? (
                          <IconLoader2 size={15} stroke={2} className="spin" />
                        ) : (
                          <IconTrash size={15} stroke={2} />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>

    {preview && (
      <ImageLightbox
        src={preview.src}
        getDownloadUrl={() => getFileUrlFresh(preview.filename, true)}
        filename={preview.filename}
        onClose={() => setPreview(null)}
      />
    )}
    {pdfPreview && (
      <PdfLightbox
        src={pdfPreview.src}
        getDownloadUrl={() => getFileUrlFresh(pdfPreview.filename, true)}
        filename={pdfPreview.filename}
        onClose={() => setPdfPreview(null)}
      />
    )}
    </>
  );
};

export { AttachmentsUploader };
